import * as Highstock from 'highcharts/highstock';
import { DERDemandReader } from '../../../services/der-demand-reader';
import { ChartService, ChartStyle } from '../../../services/chart.service';
import { DemandSeriesSelectors } from './demand-series-selectors';
import { CombinedDemandStats, OptimizationStats, StandardSeries } from '../../../domain/stats';
import { Program } from '../../../domain/cloud-topology';
import * as moment_timezone from 'moment-timezone';
import { SeriesOptionsType } from 'highcharts';

(window as any).moment = moment_timezone;

export class DemandChart {
  private series = {
    point: {
      events: {
        mouseOut: ChartService.mouseOut(),
        mouseOver: ChartService.mouseOver(),
      },
    },
    dataGrouping: {
      dateTimeLabelFormats: {
        minute: ['%A, %b %e, %l:%M %P', '%A, %b %e, %l:%M %P', '-%l:%M %P (' + ChartService.timezone + ')'],
        hour: ['%A, %b %e, %l:%M %P', '%A, %b %e, %l:%M %P', '-%l:%M %P (' + ChartService.timezone + ')'],
        day: ['%A, %b %e, %l:%M %P', '%A, %b %e, %l:%M %P', '-%l:%M %P (' + ChartService.timezone + ')'],
        week: ['%A, %b %e, %l:%M %P', '%A, %b %e, %l:%M %P', '-%l:%M %P (' + ChartService.timezone + ')'],
      },
    },
  };
  public SITE_METER_COLOR = '#0092A1';
  public BUILDING_COLOR = '#5738ff';
  public STORAGE_COLOR = '#3a7523';
  public SOLAR_COLOR = '#efc042';
  public FUELCELL_COLOR = '#2cfef9';
  public LOAD_BEFORE_BATTERY_COLOR = '#636363';
  public COMMITTED_DEMAND_COLOR = '#ff006e';
  public UPPER_BOUND_COLOR = '#fd6600';
  public FUTURE_PLOT_BAND_COLOR = '#c8c8c8';

  private chartObject: Highcharts.Chart;

  constructor(chartId: string,
              private readonly combinedDemand: CombinedDemandStats,
              private optimizationStats: OptimizationStats,
              private selectors: DemandSeriesSelectors,
              private program?: Program) {
    this.chartObject = Highstock.chart(chartId, {
      navigation: {},
      credits: {
        enabled: false,
      },
      chart: {
        panning: {
          enabled: true,
          type: 'xy',
        },
        panKey: 'shift',
        backgroundColor: ChartStyle.CHART_BACKGROUND,
      },
      title: {
        text: (this.program != null) ? `Aggregate Site Demand` : null,
      },
      plotOptions: {
        series: this.series,
      },
      series: this.buildSeries(),
      tooltip: {
        xDateFormat: '%Y-%m-%d %H:%M:%S (' + ChartService.timezone + ')',
        valueDecimals: 2,
        split: false,
        shared: true,
      },
      xAxis: {
        plotBands: [{
          from: Date.now(),
          to: this.combinedDemand.demand[this.combinedDemand.demand.length - 1][0], // Last X value in series
          color: this.FUTURE_PLOT_BAND_COLOR,
          zIndex: 0,
          id: 'future_plot_band',
        }],
        type: 'datetime',
        tickLength: 0,
      },
      yAxis: {
        opposite: false,
        title: {
          text: 'kW',
        },
        labels: {
          align: 'left',
          x: 0,
          y: -2,
        },
      },
    });
  }

  update() {
    this.chartObject.update({ series: this.buildSeries() }, true, true);
  }

  exportData(interval: string): Array<StandardSeries> {
    const demandDataExport: StandardSeries[] = [];
    if (this.selectors) {
      if (this.selectors.siteMeter) {
        demandDataExport.push({
          name: 'Site Meter(kW) - ' + interval + ' minutes',
          data: this.combinedDemand.adjustedDemand,
        });
      }
      if (this.selectors.buildingDemand) {
        demandDataExport.push({
          name: 'Building Demand(kW) - ' + interval + ' minutes',
          data: this.combinedDemand.demand,
        });
      }
      if (this.selectors.storage) {
        demandDataExport.push({
          name: 'Storage(kW) - ' + interval + ' minutes',
          data: DERDemandReader.storage(this.combinedDemand),
        });
      }
      if (this.selectors.solar) {
        demandDataExport.push({
          name: 'Solar(kW) - ' + interval + ' minutes',
          data: DERDemandReader.solar(this.combinedDemand),
        });
      }
      if (this.selectors.fuelCell) {
        demandDataExport.push({
          name: 'Fuel Cell(kW) - ' + interval + ' minutes',
          data: DERDemandReader.fuelCell(this.combinedDemand),
        });
      }
      if (this.selectors.loadBeforeBattery) {
        // tslint:disable-next-line:max-line-length
        demandDataExport.push({
          name: 'Load Before Battery(kW) - ' + interval + ' minutes',
          data: DERDemandReader.loadBeforeBattery(this.combinedDemand),

        });
      }
      if (this.selectors.committedDemand) {
        demandDataExport.push({ name: 'Committed Demand(kW)', data: this.optimizationStats.committedDemand });
      }
      if (this.selectors.upperBound) {
        demandDataExport.push({ name: 'Upper Bound(kW)', data: this.optimizationStats.upperBound });
      }
    }
    return demandDataExport;
  }

  resize() {
    this.chartObject.reflow();
  }

  private buildSeries(): Highcharts.SeriesOptionsType[] {
    const series: Highcharts.SeriesOptionsType[] = [];
    if (this.selectors) {
      if (this.selectors.siteMeter) {
        series.push(this.buildSerie('Site Meter', this.SITE_METER_COLOR, this.combinedDemand.adjustedDemand));
      }
      if (this.selectors.buildingDemand) {
        series.push(this.buildSerie('Building Demand', this.BUILDING_COLOR, this.combinedDemand.demand));
      }
      if (this.selectors.storage) {
        series.push(this.buildSerie('Storage', this.STORAGE_COLOR, DERDemandReader.storage(this.combinedDemand)));
      }
      if (this.selectors.solar) {
        series.push(this.buildSerie('Solar', this.SOLAR_COLOR, DERDemandReader.solar(this.combinedDemand)));
      }
      if (this.selectors.fuelCell) {
        series.push(this.buildSerie('Fuel Cell', this.FUELCELL_COLOR, DERDemandReader.fuelCell(this.combinedDemand)));
      }
      if (this.selectors.loadBeforeBattery) {
        series.push(this.buildSerie('Load Before Battery', this.LOAD_BEFORE_BATTERY_COLOR,
          DERDemandReader.loadBeforeBattery(this.combinedDemand)));
      }
      if (this.selectors.committedDemand) {
        series.push(this.buildSerie('Committed Demand', this.COMMITTED_DEMAND_COLOR, this.optimizationStats.committedDemand));
      }
      if (this.selectors.upperBound) {
        series.push(this.buildSerie('Upper Bound', this.UPPER_BOUND_COLOR, this.optimizationStats.upperBound));
      }
    }
    return series;
  }

  private buildSerie(name, color, data): SeriesOptionsType {
    return {
      type: 'line',
      data: data,
      name: name,
      color: color,
      connectNulls: true,
      dashStyle: 'Solid',
      zoneAxis: 'x',
      zones: [{
        value: Date.now(),
      }, {
        dashStyle: 'ShortDash',
      }],
    };
  }
}
